// =============================================================================
// services/push.ts — push notification registration (bare-RN, native-backed)
//
// HOW PUSH WORKS IN THIS APP (no expo-notifications, no FCM, no extra pod):
//   The NATIVE AppDelegate owns the device tokens. It:
//     • requests notification permission + registers for remote (APNs) pushes,
//     • registers a PushKit VoIP token (for CallKit ringing of 1:1 calls),
//     • reads the session JWT from the SAME Keychain slot this app writes
//       (services/secure-store.ts) and POSTs both tokens straight to
//       POST /notifications/push/register-token.
//   So tokens never travel through JS (and never over a spoofable URL scheme).
//
// THIS MODULE's job is the THIN coordination layer the rest of the app already
// imports (Boot's boot effect calls registerForPush(); logout() lazily requires
// unregisterPush()). The export surface is UNCHANGED so no call site moves:
//   - registerForPush(): Promise<void>       — boot-time (re)registration nudge
//   - unregisterPush(token?): Promise<void>  — sign-out de-registration
//   - registerToken(token): Promise<void>    — legacy single-token upsert (Expo)
//
// registerForPush() pings native via an internal URL (cryb://internal/register-push)
// — the ONLY supported JS→native channel without a custom module. Native then
// (re)registers now that a session JWT exists (covers the fresh-login case where
// the token arrived before the user logged in). Cold launches re-register anyway.
//
// Every export degrades silently and NEVER throws — push is non-critical.
// =============================================================================
import { Platform, Linking } from 'react-native';
import { api } from './api';

// The push token currently registered with the backend this session (legacy
// single-token path). Null unless registerToken() ran.
let registeredToken: string | null = null;

/**
 * Boot-time registration nudge. On iOS this signals the native layer (which owns
 * the APNs + VoIP tokens) to (re)register the device against the backend now that
 * an authenticated session exists. No-op-safe on other platforms. NEVER throws.
 */
export async function registerForPush(): Promise<void> {
  try {
    if (Platform.OS === 'ios') {
      // JS → native: AppDelegate handles cryb://internal/register-push by
      // re-registering for remote notifications and POSTing the current
      // APNs/VoIP tokens (it reads the session JWT from the Keychain).
      await Linking.openURL('cryb://internal/register-push');
    }
  } catch {
    // best-effort: a cold launch (or the next foreground) re-registers anyway.
  }
}

/**
 * Legacy single-token upsert (kept for the Expo token contract). Native iOS
 * registration goes through /push/register-token instead; this remains so the
 * export contract is stable and any non-native token source can still register.
 * Never throws.
 */
async function registerToken(token: string): Promise<void> {
  try {
    await api('/notifications/push/register', {
      method: 'POST',
      body: JSON.stringify({ token, platform: Platform.OS }),
    });
    registeredToken = token;
  } catch {
    // best-effort
  }
}
export { registerToken };

/**
 * Best-effort de-registration on sign-out so the signed-out account stops
 * receiving this device's pushes. With no token argument the backend clears this
 * user's registered tokens. NEVER throws.
 */
export async function unregisterPush(token?: string): Promise<void> {
  try {
    const t = token ?? registeredToken;
    await api('/notifications/push/unregister', {
      method: 'POST',
      // No token → backend removes this user's tokens (logout on this device).
      body: JSON.stringify(t ? { token: t } : {}),
    });
  } catch {
    // best-effort
  } finally {
    registeredToken = null;
  }
}
